// ייצוא XLSX בצד לקוח, בלי תלויות (ADAPT מ-v1 answer/web/xlsx.js): ZIP store, גיליון rightToLeft,
// טיפוס התא נגזר מהצהרת העמודה ולא מהערך (id "007" נשאר "007"), "נכון ל-" ושורת היקף בראש הגיליון.
import type { TableBlock } from '@aibify/core';
import { toNumber } from './format.ts';

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

const enc = new TextEncoder();

function zip(files: { name: string; data: string }[]): Blob {
  const parts: Uint8Array[] = [];
  const central: { nameB: Uint8Array; crc: number; size: number; offset: number }[] = [];
  let offset = 0;
  const u16 = (n: number) => [n & 0xff, (n >> 8) & 0xff];
  const u32 = (n: number) => [n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >>> 24) & 0xff];

  for (const f of files) {
    const nameB = enc.encode(f.name);
    const dataB = enc.encode(f.data);
    const crc = crc32(dataB);
    const local = [
      ...u32(0x04034b50), ...u16(20), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(crc), ...u32(dataB.length), ...u32(dataB.length),
      ...u16(nameB.length), ...u16(0),
    ];
    parts.push(new Uint8Array(local), nameB, dataB);
    central.push({ nameB, crc, size: dataB.length, offset });
    offset += local.length + nameB.length + dataB.length;
  }

  const dir: number[] = [];
  for (const c of central) {
    dir.push(...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0), ...u16(0), ...u16(0), ...u16(0),
      ...u32(c.crc), ...u32(c.size), ...u32(c.size),
      ...u16(c.nameB.length), ...u16(0), ...u16(0), ...u16(0), ...u16(0), ...u32(0), ...u32(c.offset),
      ...c.nameB);
  }
  const dirB = new Uint8Array(dir);
  const endB = new Uint8Array([
    ...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(central.length), ...u16(central.length),
    ...u32(dirB.length), ...u32(offset), ...u16(0),
  ]);

  const total = offset + dirB.length + endB.length;
  const out = new Uint8Array(total);
  let pos = 0;
  for (const p of parts) { out.set(p, pos); pos += p.length; }
  out.set(dirB, pos); pos += dirB.length;
  out.set(endB, pos);
  return new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const colName = (i: number) => {
  let s = '';
  let n = i + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};
const NUMERIC = new Set(['number', 'currency', 'percent']);

function cellXml(ref: string, value: unknown, type: string, styleId = 0): string {
  const s = styleId ? ` s="${styleId}"` : '';
  if (value === null || value === undefined || value === '') return `<c r="${ref}"${s}/>`;
  const n = NUMERIC.has(type) ? toNumber(value) : null;
  if (n !== null) return `<c r="${ref}"${s}><v>${n}</v></c>`;
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(value)}</t></is></c>`;
}

/** noteHe: שורת הסבר לתצוגה מסוננת/ממוינת (kit/table.js viewNote) — הגיליון הוא מה שנראה על המסך. */
export type XlsxMeta = { asOfText: string; exportedBy?: string; noteHe?: string };

export function buildXlsx(block: TableBlock, meta: XlsxMeta): Blob {
  const cols = block.columns;
  const rows = block.rows;
  const header: unknown[][] = [
    [`aibify · ${block.titleHe ?? 'טבלה'}`],
    [meta.asOfText],
    ['יוצא בידי', meta.exportedBy ?? '', 'בתאריך', new Date().toISOString()],
    ['הטבלה היא פרוסה של מה שהמייצא מורשה לראות, ואינה מספר של כל העסק.'],
    ...(block.truncated ? [[`הוצאו ${rows.length} שורות מתוך ${block.totalRows ?? rows.length}.`]] : []),
    ...(meta.noteHe ? [[meta.noteHe]] : []),
    [],
  ];
  const xml: string[] = [];
  let r = 1;
  for (const line of header) {
    xml.push(`<row r="${r}">${line.map((v, i) => cellXml(colName(i) + r, v, 'text', i === 0 ? 2 : 0)).join('')}</row>`);
    r++;
  }
  xml.push(`<row r="${r}">${cols.map((c, i) => cellXml(colName(i) + r, c.labelHe, 'text', 1)).join('')}</row>`);
  r++;
  for (const row of rows) {
    xml.push(`<row r="${r}">${cols.map((c, i) => cellXml(colName(i) + r, row[c.key], c.type)).join('')}</row>`);
    r++;
  }
  const sheet = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetViews><sheetView rightToLeft="1" workbookViewId="0" tabSelected="1"/></sheetViews>
<cols>${cols.map((c, i) => `<col min="${i + 1}" max="${i + 1}" width="${Math.min(42, Math.max(12, c.labelHe.length + 6))}" customWidth="1"/>`).join('')}</cols>
<sheetData>${xml.join('')}</sheetData>
</worksheet>`;
  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="3"><font><sz val="11"/><name val="Arial"/></font><font><b/><sz val="11"/><name val="Arial"/></font><font><sz val="9"/><color rgb="FF808080"/><name val="Arial"/></font></fonts>
<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/><xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

  return zip([
    { name: "[Content_Types].xml", data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>` },
    { name: "_rels/.rels", data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>` },
    { name: "xl/workbook.xml", data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="aibify" sheetId="1" r:id="rId1"/></sheets></workbook>` },
    { name: "xl/_rels/workbook.xml.rels", data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>` },
    { name: "xl/styles.xml", data: styles },
    { name: "xl/worksheets/sheet1.xml", data: sheet },
  ]);
}
